import type { FramingBoundaryWithMetadata } from "building-types/framing/framingMetaDataTypes";
import type { StudEndHit, StudEndTrimContext } from "building-types/framing/wallFramingStudEndTrimTypes";
import { MIN_FRAMING_STUD_LENGTH } from "building-types/framing/wallFramingTypes";
import type {
  OpeningVerticalMemberLayoutContext,
  OpeningVerticalMemberRail,
} from "building-types/framing/wallFramingVerticalMemberTypes";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "geom/containment";
import { COMPACTNESS_EPSILON, DIST_EPSILON } from "geom/core";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2ByTwoPoints, polyLine2Length } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { computeProjectedDistance, layoutPointFromPerpParallel } from "../framingLayoutUtils";
import { trimStudSegmentAtPerpendicularPlateEnds } from "./wallFramingStudEndTrim";

const SLIGHTLY_LESS_THAN_HALF = 0.5 - DIST_EPSILON;

function parallelAtLayoutPoint(x: number, y: number, planeOrigin: Vec2, framingDirection: Vec2): number {
  const point: Vec2 = [x, y];
  return computeProjectedDistance(point, planeOrigin, framingDirection);
}

function clampVerticalSegmentBottomParallel(
  segment: PolyLine2,
  minBottomParallel: number,
  ctx: OpeningVerticalMemberLayoutContext,
): PolyLine2 {
  const startParallel = parallelAtLayoutPoint(segment[0], segment[1], ctx.planeOrigin, ctx.framingDirection);
  if (startParallel >= minBottomParallel - DIST_EPSILON) {
    return segment;
  }
  const perpDistance = computeProjectedDistance([segment[0], segment[1]], ctx.planeOrigin, ctx.perpDirection);
  const [x0, y0] = layoutPointFromPerpParallel(
    perpDistance,
    minBottomParallel,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  return polyLine2ByTwoPoints(x0, y0, segment[2], segment[3]);
}

/**
 * Ray-cast vertical cavity segment at a stud/perp location, trim perpendicular plate ends,
 * and optionally clamp parallel extents (e.g. top cripples below header vs sloped top plate).
 */
export function makeOpeningVerticalMemberRail(
  ctx: OpeningVerticalMemberLayoutContext,
  perpDistance: number,
  minBottomParallel: number,
  maxTopParallel?: number,
): OpeningVerticalMemberRail | undefined {
  const { perimeter, studEndTrimCtx, planeOrigin, perpDirection, framingDirection } = ctx;
  if (!perimeter || !studEndTrimCtx) {
    return fallbackVerticalMemberRail(ctx, perpDistance, minBottomParallel, maxTopParallel);
  }

  const [rayX, rayY] = layoutPointFromPerpParallel(
    perpDistance,
    minBottomParallel,
    planeOrigin,
    perpDirection,
    framingDirection,
  );
  const cavitySegment = computeVerticalCavitySegmentAtPoint(perimeter, studEndTrimCtx, rayX, rayY, framingDirection);
  if (!cavitySegment) {
    return fallbackVerticalMemberRail(ctx, perpDistance, minBottomParallel, maxTopParallel);
  }

  let { segment } = cavitySegment;
  let startHit: StudEndHit | undefined = cavitySegment.startHit;
  let endHit: StudEndHit | undefined = cavitySegment.endHit;
  segment = clampVerticalSegmentBottomParallel(segment, minBottomParallel, ctx);

  if (maxTopParallel !== undefined) {
    const endParallel = parallelAtLayoutPoint(segment[2], segment[3], planeOrigin, framingDirection);
    if (endParallel > maxTopParallel + DIST_EPSILON) {
      const [x1, y1] = layoutPointFromPerpParallel(
        perpDistance,
        maxTopParallel,
        planeOrigin,
        perpDirection,
        framingDirection,
      );
      segment = polyLine2ByTwoPoints(segment[0], segment[1], x1, y1);
      endHit = undefined;
    }
  }

  if (polyLine2Length(segment) < MIN_FRAMING_STUD_LENGTH) {
    return undefined;
  }

  const startParallel = parallelAtLayoutPoint(segment[0], segment[1], planeOrigin, framingDirection);
  if (startParallel > minBottomParallel + DIST_EPSILON) {
    startHit = undefined;
  }

  return { polyline: segment, startHit, endHit };
}

function fallbackVerticalMemberRail(
  ctx: OpeningVerticalMemberLayoutContext,
  perpDistance: number,
  minBottomParallel: number,
  maxTopParallel: number | undefined,
): OpeningVerticalMemberRail {
  const topParallel = maxTopParallel ?? ctx.studTopParallel;
  const [x0, y0] = layoutPointFromPerpParallel(
    perpDistance,
    minBottomParallel,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  const [x1, y1] = layoutPointFromPerpParallel(
    perpDistance,
    topParallel,
    ctx.planeOrigin,
    ctx.perpDirection,
    ctx.framingDirection,
  );
  return { polyline: polyLine2ByTwoPoints(x0, y0, x1, y1) };
}

function computeVerticalCavitySegmentAtPoint(
  perimeter: FramingBoundaryWithMetadata,
  studEndTrimCtx: StudEndTrimContext,
  rayX: number,
  rayY: number,
  framingDirection: Vec2,
): { segment: PolyLine2; startHit: StudEndHit; endHit: StudEndHit } | undefined {
  const slightlyBiggerOuterPoly = offsetPolyLine2(perimeter.polyline, SLIGHTLY_LESS_THAN_HALF * COMPACTNESS_EPSILON);
  const wt1 = 1.0 - COMPACTNESS_EPSILON;
  const wt2 = COMPACTNESS_EPSILON;

  const intersections = intersectRay2PolyLine2(
    perimeter.polyline,
    rayX,
    rayY,
    framingDirection[0],
    framingDirection[1],
  );
  ray2PolyLine2IntersectionMakeUnique(intersections);

  for (let i = 0; i < intersections.length - 1; i++) {
    const startIntersection = intersections[i];
    const endIntersection = intersections[i + 1];
    const { x: x1, y: y1 } = startIntersection;
    const { x: x2, y: y2 } = endIntersection;
    const startHit: StudEndHit = {
      edgeIndex: startIntersection.edgeIndex,
      x: x1,
      y: y1,
    };
    const endHit: StudEndHit = {
      edgeIndex: endIntersection.edgeIndex,
      x: x2,
      y: y2,
    };

    const contBeg = classifyContainmentVec2PolyLine2(wt1 * x1 + wt2 * x2, wt1 * y1 + wt2 * y2, slightlyBiggerOuterPoly);
    const contEnd = classifyContainmentVec2PolyLine2(wt1 * x2 + wt2 * x1, wt1 * y2 + wt2 * y1, slightlyBiggerOuterPoly);

    if (contBeg !== Vec2PolyLine2ContainmentClass.INSIDE || contEnd !== Vec2PolyLine2ContainmentClass.INSIDE) {
      continue;
    }

    const rawSegment = polyLine2ByTwoPoints(x1, y1, x2, y2);
    const segment = trimStudSegmentAtPerpendicularPlateEnds(rawSegment, startHit, endHit, studEndTrimCtx);
    if (polyLine2Length(segment) < MIN_FRAMING_STUD_LENGTH) {
      continue;
    }

    return { segment, startHit, endHit };
  }

  return undefined;
}
